import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import AdminNavIcon from "./AdminNavIcon";

const TOOLTIP_DELAY = 190;

function SidebarAction({
  label,
  icon,
  active = false,
  current = active,
  badge,
  to,
  buttonRef,
  className = "",
  ...props
}) {
  const localRef = useRef(null);
  const timerRef = useRef(null);
  const [tooltipPosition, setTooltipPosition] = useState(null);
  const tooltipId = `admin-sidebar-tooltip-${String(label).toLowerCase().replace(/[^a-zа-яё0-9]+/gi, "-")}`;

  const setRefs = (node) => {
    localRef.current = node;
    if (typeof buttonRef === "function") buttonRef(node);
    else if (buttonRef) buttonRef.current = node;
  };

  const updateTooltipPosition = useCallback(() => {
    const rect = localRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltipPosition({ left: rect.right + 10, top: rect.top + rect.height / 2 });
  }, []);

  const showTooltip = () => {
    window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(updateTooltipPosition, TOOLTIP_DELAY);
  };

  const hideTooltip = () => {
    window.clearTimeout(timerRef.current);
    setTooltipPosition(null);
  };

  useEffect(() => () => window.clearTimeout(timerRef.current), []);
  const isTooltipVisible = Boolean(tooltipPosition);
  useEffect(() => {
    if (!isTooltipVisible) return undefined;
    const update = () => updateTooltipPosition();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [isTooltipVisible, updateTooltipPosition]);

  const commonProps = {
    ...props,
    ref: setRefs,
    className: `admin-workspace__nav-button${active ? " admin-workspace__nav-button--active" : ""}${className ? ` ${className}` : ""}`,
    "aria-label": label,
    "aria-describedby": tooltipPosition ? tooltipId : undefined,
    "aria-current": current ? "page" : undefined,
    onMouseEnter: showTooltip,
    onMouseLeave: hideTooltip,
    onFocus: showTooltip,
    onBlur: hideTooltip,
  };

  const content = (
    <>
      <AdminNavIcon name={icon} />
      {badge !== undefined && (
        <span className="admin-workspace__nav-badge" aria-label={`${badge} элементов`}>{badge}</span>
      )}
    </>
  );

  return (
    <>
      {to ? (
        <Link {...commonProps} to={to}>{content}</Link>
      ) : (
        <button {...commonProps} type="button">{content}</button>
      )}
      {tooltipPosition && createPortal(
        <div
          id={tooltipId}
          className="admin-sidebar-tooltip"
          role="tooltip"
          style={{ left: tooltipPosition.left, top: tooltipPosition.top }}
        >
          {label}
        </div>,
        document.body
      )}
    </>
  );
}

function AdminSidebar({
  sections,
  activeSection,
  onSectionChange,
  theme,
  toggleTheme,
  selectionCount,
  onOpenSelection,
  selectionButtonRef,
  selectionOpen,
}) {
  const isDark = theme === "dark";

  return (
    <aside className="admin-workspace__sidebar" aria-label="Навигация админки">
      <nav className="admin-workspace__nav" aria-label="Разделы админки">
        {sections.map((section) => (
          <SidebarAction
            key={section.key}
            label={section.label}
            icon={section.key}
            active={activeSection === section.key}
            onClick={() => onSectionChange(section.key)}
          />
        ))}
      </nav>

      <div className="admin-workspace__sidebar-tools">
        <SidebarAction
          buttonRef={selectionButtonRef}
          label="Рабочая подборка"
          icon="workspace"
          badge={selectionCount}
          active={selectionOpen}
          current={false}
          aria-haspopup="dialog"
          aria-controls="workspace-selection-drawer"
          aria-expanded={selectionOpen}
          onClick={onOpenSelection}
        />
      </div>

      <div className="admin-workspace__sidebar-footer">
        <SidebarAction
          label={isDark ? "Включить светлую тему" : "Включить тёмную тему"}
          icon={isDark ? "sun" : "moon"}
          aria-pressed={isDark}
          onClick={toggleTheme}
        />
        <SidebarAction label="На карту" icon="map" to="/" />
      </div>
    </aside>
  );
}

export default AdminSidebar;
